import React from "react"
import dayjs from "dayjs"
import { useSuspendingLiveQuery, useLiveQueryImmediate } from "@/db/hooks"
import { useDayStore } from "@/background/stores/dayStore"
import {
  db,
  type DbObject,
  type DbStartAtIndex,
  ObjectType,
  ObjectStatus,
  StartAtType,
} from "@/db/schema"
import {
  queryTodayTasks,
  queryEveningTasks,
  queryUpcomingTasks,
  querySomedayTasks,
  queryAnydayTasks,
  queryInbox as queryInboxOp,
  queryLogbook as queryLogbookOp,
  queryTrash as queryTrashOp,
  parseDbDate,
} from "@totodo/operations"
import type { ViewId } from "../viewRegistry"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A single item in a view's task list */
export interface ViewItem {
  task: DbObject
  /** Display sort order */
  sortIndex: number
  /** startAtIndex metadata (present for date-scheduled tasks) */
  startAtData?: DbStartAtIndex
}

/** Data returned from useViewData */
export interface ViewData {
  /** Primary items for this view */
  items: ViewItem[]
  /** Secondary "later"/someday section (only for project/list container views) */
  laterItems: ViewItem[]
}

const EMPTY_VIEW_DATA: ViewData = { items: [], laterItems: [] }

/**
 * Unwrap an OpResult<ViewData> from the shared operations layer.
 * On failure, returns empty data rather than throwing (defensive for queries).
 */
async function unwrapViewOp(
  op: Promise<import("@totodo/operations").OpResult<ViewData>>,
): Promise<ViewData> {
  const result = await op
  if (!result.ok) {
    console.error("[useViewData] shared op failed:", result.error)
    return EMPTY_VIEW_DATA
  }
  return result.data
}

// ---------------------------------------------------------------------------
// Shared query wrappers — delegate to @totodo/operations (single source of truth)
// ---------------------------------------------------------------------------

async function queryToday(): Promise<ViewData> {
  return unwrapViewOp(queryTodayTasks())
}

async function queryEvening(): Promise<ViewData> {
  return unwrapViewOp(queryEveningTasks())
}

async function queryUpcoming(): Promise<ViewData> {
  return unwrapViewOp(queryUpcomingTasks())
}

async function querySomeday(): Promise<ViewData> {
  return unwrapViewOp(querySomedayTasks())
}

async function queryAnyday(): Promise<ViewData> {
  return unwrapViewOp(queryAnydayTasks())
}

async function queryInbox(): Promise<ViewData> {
  return unwrapViewOp(queryInboxOp())
}

async function queryLogbook(): Promise<ViewData> {
  return unwrapViewOp(queryLogbookOp())
}

async function queryTrash(): Promise<ViewData> {
  return unwrapViewOp(queryTrashOp())
}

// ---------------------------------------------------------------------------
// Client-only helpers for container/area/laterProjects queries
// ---------------------------------------------------------------------------

/**
 * Check that a startAtIndex entry has a valid (non-empty) startAt timestamp.
 */
function hasValidStartAt(entry: DbStartAtIndex): boolean {
  return parseDbDate(entry.startAt) !== undefined
}

/**
 * Container view (Project/List): tasks at parentPath = "root/{id}".
 * Splits into default section (startAtType ≠ Someday) + later section (Someday).
 */
async function queryContainer(containerId: string): Promise<ViewData> {
  const parentPath = `root/${containerId}`

  // 1. Get all non-deleted ACTIVE items in this container
  // Logged (completed/canceled) items are loaded separately via useContainerLoggedItems.
  // Recurring templates (isRecurringTemplate=1) are included regardless of status
  // so they show in their container (dimmed, with ↻ icon).
  const tasks = await db.totodo
    .where("[parentPath+isDeleted]")
    .equals([parentPath, 0])
    .filter(
      (t) =>
        (t.type === ObjectType.Task || t.type === ObjectType.Section) &&
        (t.status === ObjectStatus.Unfinished || t.isRecurringTemplate === 1),
    )
    .toArray()

  // 2. Get startAtIndex data for sorting + section splitting
  const startAtIds = tasks.filter((t) => t.id).map((t) => `#${t.id}`)
  const startAtEntries = await db.startAtIndex.bulkGet(startAtIds)
  const entryMap = new Map<string, DbStartAtIndex>()
  for (let i = 0; i < startAtIds.length; i++) {
    const sid = startAtIds[i]
    if (startAtEntries[i] && sid) {
      entryMap.set(sid.slice(1), startAtEntries[i]!)
    }
  }

  // 3. Split into default + later (someday)
  const defaultItems: ViewItem[] = []
  const laterItems: ViewItem[] = []

  for (const t of tasks) {
    if (!t.id) continue
    const entry = entryMap.get(t.id)

    if (entry?.type === StartAtType.Someday) {
      laterItems.push({
        task: t,
        sortIndex: entry.index,
        startAtData: entry,
      })
    } else {
      defaultItems.push({
        task: t,
        sortIndex: t.index,
        startAtData: entry,
      })
    }
  }

  defaultItems.sort((a, b) => a.sortIndex - b.sortIndex)
  laterItems.sort((a, b) => a.sortIndex - b.sortIndex)

  return { items: defaultItems, laterItems }
}

/**
 * Area view: shows unfinished tasks directly under the area.
 * Projects/lists are displayed separately by AreaProjectList.
 */
async function queryArea(areaId: string): Promise<ViewData> {
  const parentPath = `root/${areaId}`

  const children = await db.totodo
    .where("[parentPath+isDeleted]")
    .equals([parentPath, 0])
    .filter(
      (t) =>
        (t.type === ObjectType.Task || t.type === ObjectType.Section) &&
        t.status === ObjectStatus.Unfinished,
    )
    .sortBy("index")

  const items: ViewItem[] = children
    .filter((t) => t.id != null)
    .map((t) => ({
      task: t,
      sortIndex: t.index,
    }))

  return { items, laterItems: [] }
}

/**
 * Later Projects: projects/lists that are scheduled for the future (Upcoming)
 * or deferred to Someday. Shows container objects grouped by schedule type.
 *
 * Query strategy:
 * 1. Get all startAtIndex entries (both Default-future and Someday)
 * 2. Resolve IDs to containers (Project/List only, Unfinished, not deleted)
 * 3. Split into upcoming (Default+future startAt) vs someday (Someday type)
 * 4. Sort upcoming by startAt ASC, someday by sortIndex
 */
async function queryLaterProjects(): Promise<ViewData> {
  const todayEnd = dayjs().endOf("day").valueOf()

  // Get ALL startAtIndex entries — we filter client-side for Project/List
  const allEntries = await db.startAtIndex.toArray()

  // Filter to entries with valid dates that represent "later" scheduling
  const laterEntries = allEntries.filter((e) => {
    if (!hasValidStartAt(e)) return false
    // Someday type: always "later"
    if (e.type === StartAtType.Someday) return true
    // Default type with future date: "upcoming" project
    if (e.type === StartAtType.Default && (parseDbDate(e.startAt) ?? 0) > todayEnd)
      return true
    return false
  })

  if (laterEntries.length === 0) return { items: [], laterItems: [] }

  // ── Determine which projects belong to an area (positional) ──
  // Walk the sidebar menu sorted by privateIndex — items between two areas
  // belong to the first area. Only free-standing (no area) items are shown.
  const menuItems = await db.totodo
    .where("[parentPath+type+isDeleted]")
    .anyOf([
      ["root", ObjectType.Area, 0],
      ["root", ObjectType.Project, 0],
      ["root", ObjectType.List, 0],
    ])
    .toArray()
  const privateIds = menuItems.map((i) => `#${i.id}`)
  const sortEntries = await db.privateIndex.bulkGet(privateIds)
  const sortMap = new Map<string, number>()
  privateIds.forEach((pid, i) => {
    if (sortEntries[i]) sortMap.set(pid, sortEntries[i]!.index)
  })
  menuItems.sort((a, b) => {
    const aIdx = sortMap.get(`#${a.id}`) ?? a.index
    const bIdx = sortMap.get(`#${b.id}`) ?? b.index
    return aIdx - bIdx
  })
  // Walk to find area-scoped IDs
  const areaScopedIds = new Set<string>()
  let underArea = false
  for (const item of menuItems) {
    if (item.type === ObjectType.Area) {
      underArea = true
    } else if (underArea && item.id) {
      areaScopedIds.add(item.id)
    }
  }

  // Resolve IDs (strip # prefix)
  const entryMap = new Map(laterEntries.map((e) => [e.id!.slice(1), e]))
  const objectIds = [...entryMap.keys()]
  const objects = await db.totodo.bulkGet(objectIds)

  // Split into upcoming (primary items) and someday (laterItems)
  const upcomingItems: ViewItem[] = []
  const somedayItems: ViewItem[] = []

  for (let i = 0; i < objects.length; i++) {
    const obj = objects[i]
    if (!obj?.id) continue
    if (obj.isDeleted !== 0) continue
    if (obj.status !== ObjectStatus.Unfinished) continue
    // Only projects and lists — not tasks
    if (obj.type !== ObjectType.Project && obj.type !== ObjectType.List)
      continue
    // Skip area-scoped items — only show free-standing later projects
    if (areaScopedIds.has(obj.id)) continue

    const entry = entryMap.get(obj.id)
    const viewItem: ViewItem = {
      task: obj,
      sortIndex: entry?.index ?? obj.index,
      startAtData: entry,
    }

    if (entry?.type === StartAtType.Someday) {
      somedayItems.push(viewItem)
    } else {
      upcomingItems.push(viewItem)
    }
  }

  // Upcoming: sorted by start date ASC
  upcomingItems.sort((a, b) => {
    const aDate = parseDbDate(a.startAtData?.startAt) ?? 0
    const bDate = parseDbDate(b.startAtData?.startAt) ?? 0
    return aDate - bDate || a.sortIndex - b.sortIndex
  })

  // Someday: sorted by sortIndex
  somedayItems.sort((a, b) => a.sortIndex - b.sortIndex)

  // ── Recurring project/list templates (no startAtIndex needed) ──
  // These are shown in "Upcoming" section of Later Projects (like Things 3).
  const recurringTemplates = await db.totodo
    .where("isRecurringTemplate")
    .equals(1)
    .filter(
      (t) =>
        t.isDeleted === 0 &&
        t.status === ObjectStatus.Unfinished &&
        (t.type === ObjectType.Project || t.type === ObjectType.List) &&
        !areaScopedIds.has(t.id!) &&
        !entryMap.has(t.id!), // skip if already included via startAtIndex
    )
    .toArray()

  for (const tpl of recurringTemplates) {
    upcomingItems.push({
      task: tpl,
      sortIndex: tpl.index,
    })
  }

  // Re-sort upcoming after adding recurring templates
  upcomingItems.sort((a, b) => {
    const aDate = parseDbDate(a.startAtData?.startAt) ?? 0
    const bDate = parseDbDate(b.startAtData?.startAt) ?? 0
    return aDate - bDate || a.sortIndex - b.sortIndex
  })

  return { items: upcomingItems, laterItems: somedayItems }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Layer 2 view data hook — queries Dexie based on view ID.
 *
 * Uses `useSuspendingLiveQuery` with view-specific cache key.
 * The observable cache ensures ONE Dexie subscription per unique view+container.
 * Auto-tracks table dependencies (totodo, startAtIndex) and re-fires on changes.
 *
 * Suspends until first data load — parent must have a `<Suspense>` boundary.
 *
 * @param viewId - Which view to load data for
 * @param containerId - Container ID for project/list/area views
 * @returns [ViewData, setOptimistic]
 */
export function useViewData(
  viewId: ViewId,
  containerId?: string,
): [ViewData, React.Dispatch<React.SetStateAction<ViewData | null>>] {
  return useSuspendingLiveQuery(() => {
    switch (viewId) {
      case "today":
        return queryToday()
      case "evening":
        return queryEvening()
      case "upcoming":
        return queryUpcoming()
      case "someday":
        return querySomeday()
      case "anyday":
        return queryAnyday()
      case "inbox":
        return queryInbox()
      case "logbook":
        return queryLogbook()
      case "trash":
        return queryTrash()
      case "laterProjects":
        return queryLaterProjects()
      case "project":
      case "list":
        return queryContainer(containerId ?? "")
      case "area":
        return queryArea(containerId ?? "")
      case "search":
      case "tag":
        // Search and Tag pages handle their own queries
        return Promise.resolve(EMPTY_VIEW_DATA)
      default:
        return Promise.resolve(EMPTY_VIEW_DATA)
    }
  }, ["view", viewId, containerId ?? ""])
}

// ---------------------------------------------------------------------------
// useViewDataImmediate — Non-suspending variant with placeholder
// ---------------------------------------------------------------------------

import {
  getLoadingPlaceholder,
  cacheViewPlaceholder,
} from "../loadingPlaceholders"
import { getUniqueTagsFromItems, viewShowsTagFilter } from "@/features/tags"

/**
 * Layer 2 view data hook — **non-suspending**.
 *
 * Same query logic as `useViewData`, but returns a placeholder `ViewData`
 * immediately (with `isLoading = true`) instead of suspending.
 * Once the Dexie query resolves, swaps to real data (`isLoading = false`).
 *
 * Placeholder data is served from localStorage cache when available,
 * falling back to static config in `loadingPlaceholders.ts`.
 * On every real data load the first 20 items are cached to localStorage.
 *
 * @param viewId - Which view to load data for
 * @param containerId - Container ID for project/list/area views
 * @param containerTitle - Container name to cache (project/list/area title)
 * @returns [ViewData, setOptimistic, isLoading]
 */
export function useViewDataImmediate(
  viewId: ViewId,
  containerId?: string,
  containerTitle?: string,
): [ViewData, React.Dispatch<React.SetStateAction<ViewData | null>>, boolean] {
  const placeholder = getLoadingPlaceholder(viewId, containerId)

  // Subscribe to day changes so date-based views re-query at midnight
  const today = useDayStore((s) => s.today)

  const [data, setOptimistic, isLoading] = useLiveQueryImmediate(
    () => {
      switch (viewId) {
        case "today":
          return queryToday()
        case "evening":
          return queryEvening()
        case "upcoming":
          return queryUpcoming()
        case "someday":
          return querySomeday()
        case "anyday":
          return queryAnyday()
        case "inbox":
          return queryInbox()
        case "logbook":
          return queryLogbook()
        case "trash":
          return queryTrash()
        case "laterProjects":
          return queryLaterProjects()
        case "project":
        case "list":
          return queryContainer(containerId ?? "")
        case "area":
          return queryArea(containerId ?? "")
        case "search":
        case "tag":
        case "recurring":
          return Promise.resolve(EMPTY_VIEW_DATA)
        default:
          return Promise.resolve(EMPTY_VIEW_DATA)
      }
    },
    ["view", viewId, containerId ?? "", today],
    placeholder,
  )

  // Cache real data to localStorage whenever it changes (debounced by React's commit phase)
  const isLoadingRef = React.useRef(isLoading)
  isLoadingRef.current = isLoading

  React.useEffect(() => {
    // Only cache when we have real data (not loading)
    if (isLoadingRef.current) return
    // Compute available tags for ChoosableTags cache (own tags only, best-effort)
    const viewTags = viewShowsTagFilter(viewId)
      ? getUniqueTagsFromItems(
          [...data.items, ...data.laterItems].map((vi) => ({
            allTags: vi.task.tags ?? [],
          })),
        )
      : undefined
    cacheViewPlaceholder(viewId, data, containerId, containerTitle, viewTags)
  }, [data, viewId, containerId, containerTitle])

  return [data, setOptimistic, isLoading]
}
